import React, { useState, useEffect } from "react";
import axios from "axios";
import ISubmission from "../types/ISubmission";
import { format, parseISO } from "date-fns";

interface IProps {
    loadSubmissionRoute: (data: any) => void;
    date: string;
    filter: {
        assignee: string | null;
        division: string | null;
        showUnassigned: boolean;
        selectedAddresses: string[];
        startPoint: string | null;
        endPoint: string | null;
    };
    showTempSubmission: boolean;
    isOptimizing: boolean;
}

const RoutingSubmissions = ({
    loadSubmissionRoute,
    date,
    filter: { assignee: filterAssignee, division, showUnassigned, selectedAddresses, startPoint, endPoint },
    showTempSubmission,
    isOptimizing,
}: IProps) => {
    const [submissions, setSubmissions] = useState<ISubmission[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [showSubmissions, setShowSubmissions] = useState<boolean>(false);
    const [refreshingSubmission, setRefreshingSubmission] = useState<string | null>(null); // Track which submission is being refreshed

    const formattedDate = format(new Date(date), "yyyy-MM-dd");

    const fetchSubmissions = async () => {
   
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-map/local-submissions?date=${formattedDate}&assignee=${filterAssignee}&division=${division}&showUnassigned=${showUnassigned}&addresses=${JSON.stringify(selectedAddresses)}&startPoint=${startPoint}&endPoint=${endPoint}`
            );

      
            const sortedSubmissions = [...(response.data || [])].sort((a, b) => Number(b.id) - Number(a.id));

            if (sortedSubmissions.length > 0) {
                setShowSubmissions(true);
            }
            setSubmissions(sortedSubmissions);
        } catch (error) {
            console.error("Error fetching submissions:", error);
        } finally {
            setLoading(false);
        }
    };

    const refreshSubmissionStatus = async (id: string) => {
        try {
            setRefreshingSubmission(id);

        
            const response = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-map/submission-status?id=${id}`
            );

            if (!response.data || response.status !== 200) {
                throw new Error("Invalid response from server");
            }

        

            // ✅ Update the full submission object, not just status
            setSubmissions((prev) =>
                prev.map((sub) =>
                    sub.id.toString() === id.toString() ? { ...sub, ...response.data } : sub
                )
            );

            // ✅ Send the full updated submission to the parent component
            loadSubmissionRoute({
                submission: response.data,
                filters: {
                    assignee: filterAssignee,
                    division: division,
                    showUnassigned: showUnassigned,
                    selectedAddresses: selectedAddresses,
                    startPoint: startPoint,
                    endPoint: endPoint
                }
            });

        } catch (error) {
            console.error("Error fetching updated submission status:", error);
        } finally {
            setRefreshingSubmission(null);
        }
    };

    // Auto-refresh every 5 seconds for "submitted" submissions
    // useEffect(() => {
    //     const interval = setInterval(() => {
    //         submissions.forEach((submission) => {
    //             if (submission.status === "submitted") {
    //                 refreshSubmissionStatus(submission.submission_id);
    //             }
    //         });
    //     }, 5000); // Every 5 seconds

    //     return () => clearInterval(interval); // Cleanup on unmount
    // }, [submissions]);

    useEffect(() => {
        fetchSubmissions();
    }, [formattedDate]);

    useEffect(() => {
        if (!isOptimizing) {
            fetchSubmissions();
        }
    }, [isOptimizing]);

    return (
        <>
            {/* Floating Button */}
            <button
                onClick={() => setShowSubmissions(!showSubmissions)}
                className="fixed bottom-10 right-10 z-50 flex items-center gap-2 bg-gray-100 text-gray-800 border border-gray-300 rounded px-3 py-2 shadow-md transition-all duration-300 hover:bg-gray-300 hover:w-auto group"
            >
                📜
                {submissions.length > 0 && (
                    <span className="bg-gray-200 text-gray text-xs px-3 py-1 rounded-full ml-1">
                        {submissions.length}
                    </span>
                )}
                {showTempSubmission && submissions.length === 0 && (
                    <span className="bg-gray-200 text-gray text-xs px-3 py-1 rounded-full ml-1">
                        1
                    </span>
                )}
                <span className="overflow-hidden whitespace-nowrap max-w-0 opacity-0 transition-all duration-300 group-hover:max-w-xs group-hover:opacity-100">
                    View Submissions
                </span>
            </button>

            {/* Submissions Panel */}
            {showSubmissions && (
                <div className="absolute bottom-10 right-10 w-[320px] max-h-[300px] bg-white shadow-lg border border-gray-300 p-4 z-50 rounded-md overflow-y-auto">
                    <div className="flex justify-between items-center">
                        <h3 className="text-sm font-semibold">Routing Submissions</h3>
                        <button onClick={() => setShowSubmissions(false)} className="text-gray-500 hover:text-red-500">
                            ❌
                        </button>
                    </div>

                    <div className="mt-2">
                        {loading ? (
                            <p className="text-gray-500 text-xs">Loading...</p>
                        ) : submissions.length === 0 ? (
                            <p className="text-gray-500 text-xs">No submissions found.</p>
                        ) : (
                            <ul className="space-y-2">
                                {submissions.map((submission: ISubmission) => {
                                    const formattedLocations =
                                        typeof submission.formatted_locations === "string"
                                            ? JSON.parse(submission.formatted_locations)
                                            : submission.formatted_locations || [];

                                    // Format date to local time
                                    const localDate = format(parseISO(submission.created_at), "PPpp");

                                    const statusColors: Record<string, string> = {
                                        submitted: "bg-gray-400",
                                        completed: "bg-green-500",
                                    };

                                    return (
                                        <li
                                            key={submission.id}
                                            className="flex justify-between items-center p-2 border-b border-gray-200 rounded text-xs transition"
                                        >
                                            <div className="flex flex-col">
                                                <strong className="block">{localDate}</strong>
                                                <span>{formattedLocations.length} Locations</span>
                                                <span className="flex items-center gap-2 mt-1">
                                                    <span className={`h-2 w-2 rounded-full ${statusColors[submission.status]}`} />
                                                    <span className="text-gray-600">
                                                        {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                                                    </span>
                                                </span>
                                            </div>

                                            {/* Refresh Button with Spinner */}
                                            <button
                                                onClick={() => refreshSubmissionStatus(submission.id.toString())}
                                                className="text-gray-500 hover:text-blue-500 transition"
                                            >
                                                {refreshingSubmission === submission.submission_id ? "⏳" : "🔄"}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default RoutingSubmissions;
